/**
 * Page to set or change the password.
 *
 * Copyright © Ircam Amplify 2024-2026
 */
import { AppRouteUrl } from '@/AppRoute'
import { RhfFormInput, RhfFormInputPassword } from '@/components/RhfFormInput'
import { logger, LogTag } from '@/config/logger'
import { zodResolver } from '@hookform/resolvers/zod'
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z as zod } from 'zod'
import './Login.css'
import LogInButton from './LoginButton'
import LoginDialog from './LoginDialog'
import './SetPasswordPage.css'

/**
 * The Zod schema defining the fields and their data type.
 */
const ResetPasswordSchema = zod.object({
  email: zod.email().trim(),
  password: zod.string().min(1, 'Required'),
  confirmedPassword: zod.string().min(1, 'Required'),
  code: zod.string().min(1, 'Required'),
})
type ResetPasswordType = zod.infer<typeof ResetPasswordSchema>

interface SetPasswordPageProps {
  title?: string
  description?: string
  submitButtonText?: string
}

export const SetPasswordPage = (props: SetPasswordPageProps) => {
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(window.location.search)

  // Get the needed functions defined by react-use-form,
  // by destructuring useForm return.
  const {
    register,
    control,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<ResetPasswordType>({
    mode: 'all',
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      email: urlParams.get('email') || '',
      code: '',
      password: '',
      confirmedPassword: '',
    },
  })

  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [errorTitle, setErrorTitle] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const email = watch('email')

  function closeErrorDialog() {
    setErrorTitle(null)
    setErrorMsg(null)
  }

  const submitResetPassword = async (data: ResetPasswordType) => {
    if (data.password !== data.confirmedPassword) {
      setLoading(false)
      setErrorMsg('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      await confirmResetPassword({
        username: data.email,
        newPassword: data.password,
        confirmationCode: data.code,
      })
      navigate(AppRouteUrl.LogIn)
    } catch (e: any) {
      if (e instanceof Error && e.name === 'ExpiredCodeException') {
        setErrorMsg('The reset code has expired.\nPlease request a new one.')
      } else {
        setErrorMsg(e.message)
      }
      setLoading(false)
    }
  }
  const resetPassword = handleSubmit(submitResetPassword)

  return (
    <>
      <div className="login-page set-password">
        <h1 className="login-title">{props.title ?? 'Set Password'}</h1>

        {props.description && (
          <div className="login-subtitle">{props.description}</div>
        )}

        <form onSubmit={resetPassword} className="rhf-form">
          <RhfFormInput
            title="Email address"
            id="email"
            type="string"
            register={register}
            error={errors.email}
          />

          <RhfFormInputPassword
            title="Password"
            id="password"
            register={register}
            error={errors.password}
            control={control}
          />

          <RhfFormInputPassword
            title="Confirm Password"
            id="confirmedPassword"
            register={register}
            error={errors.confirmedPassword}
            control={control}
          />

          <div className="request-code-input-container">
            <RequestCodeButton
              email={email}
              setErrorMsg={setErrorMsg}
              setErrorTitle={setErrorTitle}
              trigger={trigger}
            />
            <RhfFormInput
              className="set-password-code"
              title="Email verification code"
              id="code"
              type="string"
              register={register}
              error={errors.code}
            />
          </div>

          <div className="login-submit-container">
            <LogInButton
              text={props.submitButtonText ?? 'Set Password'}
              type="submit"
              disabled={loading || !isValid}
            />

            <LogInButton
              text="Cancel"
              toLink={AppRouteUrl.LogIn}
              disabled={loading || !isValid}
              dark={false}
            />
          </div>
        </form>
      </div>

      <LoginDialog
        title={errorTitle ?? 'Error'}
        message={errorMsg}
        onClick={closeErrorDialog}
        onClose={closeErrorDialog}
        open={errorMsg !== null}
      />
    </>
  )
}

interface RequestCodeButtonProps {
  email: string
  setErrorMsg: (msg: string) => void
  setErrorTitle: (title: string) => void
  trigger: (name: 'email') => Promise<boolean>
}

const RequestCodeButton = (props: RequestCodeButtonProps) => {
  const [isProcessing, setIsProcessing] = useState(false)

  async function requestNewCode(email: string) {
    const isEmailValid = await props.trigger('email')
    if (!isEmailValid) {
      return
    }

    setIsProcessing(true)
    try {
      await resetPassword({ username: email })
      // Reuse the Error Dialog to display acknowledgement message
      props.setErrorTitle('Code Request')
      props.setErrorMsg('A code has been requested.\nPlease check your emails.')
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      logger.error(LogTag.LogIn, message)
      props.setErrorTitle('Code Request Error')
      props.setErrorMsg(message)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <button
      className={
        'ircam-button button button-regular button-dark ' +
        'button-request-code '
      }
      disabled={isProcessing}
      type="button"
      onClick={() => {
        requestNewCode(props.email)
      }}
    >
      <div className="button-label">Request code</div>
    </button>
  )
}
